import React, { useState, useEffect } from 'react'
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  CalculatorIcon,
  ChartBarIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { accountingAPI } from '@/services/api'

interface ChartAccount {
  id: string
  code: string
  name: string
  description?: string
  account_type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | 'cost'
  parent_id?: string
  level: number
  is_active: boolean
  balance: number
}

interface JournalEntry {
  id: string
  entry_number: string
  entry_date: string
  description: string
  total_debit: number
  total_credit: number
  status: 'draft' | 'posted' | 'cancelled'
  created_at: string
}

interface BalanceSheetData {
  assets: {
    current_assets: number
    non_current_assets: number
    total_assets: number
  }
  liabilities: {
    current_liabilities: number
    non_current_liabilities: number
    total_liabilities: number
  }
  equity: {
    capital: number
    retained_earnings: number
    total_equity: number
  }
}

const AccountingDashboard: React.FC = () => {
  const [accounts, setAccounts] = useState<ChartAccount[]>([])
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheetData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'accounts' | 'journal' | 'reports'>('accounts')
  const [showJournalModal, setShowJournalModal] = useState(false)
  const [showAccountModal, setShowAccountModal] = useState(false)

  const handleNewJournalEntry = () => {
    setShowJournalModal(true)
  }

  const handleExportSPED = () => {
    alert('Funcionalidade de exportação SPED será implementada em breve!')
  }

  const handleNewAccount = () => {
    setShowAccountModal(true)
  }

  const handleNewJournalEntryFromTab = () => {
    setShowJournalModal(true)
  }

  useEffect(() => {
    const fetchAccountingData = async () => {
      try {
        setLoading(true)
        // Connect to REAL API endpoints - remove mock data usage
        // Get today's date in YYYY-MM-DD format for balance sheet reference
        const today = new Date().toISOString().split('T')[0]
        const [accountsData, journalData, balanceSheetData] = await Promise.all([
          accountingAPI.getAccounts(),
          accountingAPI.getJournalEntries(),
          accountingAPI.getBalanceSheet(today)
        ])

        // Convert API data to match interface
        const convertedAccounts: ChartAccount[] = accountsData.map(acc => ({
          id: acc.id.toString(),
          code: acc.code,
          name: acc.name,
          account_type: acc.type.toLowerCase() as any,
          level: 1,
          is_active: acc.is_active,
          balance: acc.balance
        }))

        const convertedJournalEntries: JournalEntry[] = journalData.map(entry => ({
          id: entry.id.toString(),
          entry_number: `LC${entry.id.toString().padStart(3, '0')}`,
          entry_date: entry.date,
          description: entry.description,
          total_debit: entry.total_amount,
          total_credit: entry.total_amount,
          status: 'posted' as const,
          created_at: entry.created_at
        }))

        const convertedBalanceSheet: BalanceSheetData = {
          assets: {
            current_assets: balanceSheetData.assets.current_assets.reduce((sum, acc) => sum + acc.balance, 0),
            non_current_assets: balanceSheetData.assets.non_current_assets.reduce((sum, acc) => sum + acc.balance, 0),
            total_assets: balanceSheetData.assets.total_assets
          },
          liabilities: {
            current_liabilities: balanceSheetData.liabilities.current_liabilities.reduce((sum, acc) => sum + acc.balance, 0),
            non_current_liabilities: balanceSheetData.liabilities.non_current_liabilities.reduce((sum, acc) => sum + acc.balance, 0),
            total_liabilities: balanceSheetData.liabilities.total_liabilities
          },
          equity: {
            capital: balanceSheetData.equity.equity_accounts.reduce((sum, acc) => sum + acc.balance, 0),
            retained_earnings: 0,
            total_equity: balanceSheetData.equity.total_equity
          }
        }

        setAccounts(convertedAccounts)
        setJournalEntries(convertedJournalEntries)
        setBalanceSheet(convertedBalanceSheet)
        setError(null)
      } catch (err: any) {
        console.error('Accounting API error:', err)
        setError(`Erro ao carregar dados contábeis: ${err.message}`)
        // On error, set empty state instead of mock data
        setAccounts([])
        setJournalEntries([])
        setBalanceSheet({
          assets: {
            current_assets: 0,
            non_current_assets: 0,
            total_assets: 0
          },
          liabilities: {
            current_liabilities: 0,
            non_current_liabilities: 0,
            total_liabilities: 0
          },
          equity: {
            capital: 0,
            retained_earnings: 0,
            total_equity: 0
          }
        })
      } finally {
        setLoading(false)
      }
    }

    fetchAccountingData()
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getAccountTypeLabel = (type: string) => {
    const types = {
      asset: 'Ativo',
      liability: 'Passivo',
      equity: 'Patrimônio Líquido',
      revenue: 'Receita',
      expense: 'Despesa',
      cost: 'Custo'
    }
    return types[type as keyof typeof types] || type
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted': return 'text-green-600 bg-green-50'
      case 'draft': return 'text-yellow-600 bg-yellow-50'
      case 'cancelled': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse bg-gray-200 h-8 rounded-lg w-64"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-gray-200 h-32 rounded-xl"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contabilidade</h1>
          <p className="text-gray-600">Gestão contábil completa com plano de contas, lançamentos e relatórios</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleNewJournalEntry}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Novo Lançamento
          </button>
          <button
            onClick={handleExportSPED}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Exportar SPED
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {balanceSheet && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Ativos</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(balanceSheet.assets.total_assets)}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Circulante: {formatCurrency(balanceSheet.assets.current_assets)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Passivos</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(balanceSheet.liabilities.total_liabilities)}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Circulante: {formatCurrency(balanceSheet.liabilities.current_liabilities)}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Patrimônio Líquido</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(balanceSheet.equity.total_equity)}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Capital: {formatCurrency(balanceSheet.equity.capital)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <ChartBarIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'accounts', label: 'Plano de Contas', icon: DocumentTextIcon },
              { id: 'journal', label: 'Livro Diário', icon: CalculatorIcon },
              { id: 'reports', label: 'Relatórios', icon: ChartBarIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'accounts' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Plano de Contas</h3>
                <button
                  onClick={handleNewAccount}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="w-4 h-4" />
                  Nova Conta
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Código</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Nome</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900">Tipo</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-900">Saldo</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-900">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm">{account.code}</td>
                        <td className="py-3 px-4 font-medium">{account.name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {getAccountTypeLabel(account.account_type)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">
                          {formatCurrency(account.balance)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            account.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {account.is_active ? 'Ativa' : 'Inativa'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'journal' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Lançamentos Contábeis</h3>
                <button
                  onClick={handleNewJournalEntryFromTab}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="w-4 h-4" />
                  Novo Lançamento
                </button>
              </div>

              <div className="space-y-4">
                {journalEntries.map((entry) => (
                  <div key={entry.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                            {entry.entry_number}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.status)}`}>
                            {entry.status === 'posted' ? 'Lançado' :
                             entry.status === 'draft' ? 'Rascunho' : 'Cancelado'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(entry.entry_date).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-gray-900 font-medium mb-1">{entry.description}</p>
                        <div className="flex gap-4 text-sm text-gray-600">
                          <span>Débito: {formatCurrency(entry.total_debit)}</span>
                          <span>Crédito: {formatCurrency(entry.total_credit)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                          Ver Detalhes
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Relatórios Contábeis</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { name: 'Balanço Patrimonial', description: 'Demonstração da posição patrimonial' },
                  { name: 'DRE', description: 'Demonstração do Resultado do Exercício' },
                  { name: 'DFC', description: 'Demonstração do Fluxo de Caixa' },
                  { name: 'Razão Geral', description: 'Movimentação das contas contábeis' },
                  { name: 'Balancete', description: 'Saldos das contas em período' },
                  { name: 'SPED Contábil', description: 'Arquivo para Receita Federal' }
                ].map((report, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer">
                    <h4 className="font-medium text-gray-900 mb-2">{report.name}</h4>
                    <p className="text-sm text-gray-600 mb-3">{report.description}</p>
                    <button className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      Gerar Relatório
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal para Novo Lançamento */}
      {showJournalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Novo Lançamento Contábil</h3>
            <p className="text-gray-600 mb-4">Formulário para criação de lançamento será implementado...</p>
            <button
              onClick={() => setShowJournalModal(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal para Nova Conta */}
      {showAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nova Conta Contábil</h3>
            <p className="text-gray-600 mb-4">Formulário para criação de conta será implementado...</p>
            <button
              onClick={() => setShowAccountModal(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountingDashboard